<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useThemeStore, useValueDataStore } from '@/stores'

import ContentView from './ContentView.vue'
import PharmaProgramsWidget from '@/components/widgets/pharma-programs-widget/PharmaProgramsWidget.vue'
import SpinnerOverlay from '@/components/common/SpinnerOverlay.vue'
import AxeLogo from '@/components/icons/AxeLogo.vue'
import PharmaProgramsLogo from '@/components/icons/PharmaProgramsLogo.vue'
import ExportButton from '@/components/common/ExportButton.vue'
import ExternalLinks from '@/components/common/ExternalLinks.vue'

import AmgevitaLogo from '@/components/icons/AmgevitaLogo.vue'
import HadlimaLogo from '@/components/icons/HadlimaLogo.vue'
import ProliaLogo from '@/components/icons/ProliaLogo.vue'
import MytolacLogo from '@/components/icons/MytolacLogo.vue'
import SteqeymaLogo from '@/components/icons/SteqeymaLogo.vue'
import ErrorView from './ErrorView.vue'
// import DataRefreshDatesButton from '@/components/common/DataRefreshDatesButton.vue'
import type { PharmaProgramsProduct } from '@/data'

const themeStore = useThemeStore()
const { themeColors } = themeStore
const { isValueDataStoreLoading, valueData, isError } = storeToRefs(useValueDataStore())

// Map brand names to their respective components and styles
const arrotexBrandConfigs: Record<
  string,
  {
    logo: any
    color: string
    width: string
    height: string
    glow?: boolean
    transform?: string
    averageFee?: number
  }
> = {
  Amgevita: {
    logo: AmgevitaLogo,
    color: '#2569B1',
    width: '10.9375rem',
    height: '3.125rem',
    transform: 'scale(1.3)',
    averageFee: 60
  },
  Hadlima: { logo: HadlimaLogo, color: '#B51E5E', width: '8.125rem', height: '3.125rem', averageFee: 60 },
  Prolia: { logo: ProliaLogo, color: '#117026', width: '9.0625rem', height: '3.125rem', averageFee: 15 },
  SteQeyma: { logo: SteqeymaLogo, color: '#252C78', width: '8.75rem', height: '3.75rem', averageFee: 26 },
  Other: { logo: 'All Others', color: '#B0220E', width: '10.3125rem', height: '3.125rem', glow: true }
}

// Map brand names to their respective components and styles
const otherBrandConfigs: Record<
  string,
  {
    logo: any
    width: string
    height: string
    transform?: string
    averageFee?: number
  }
> = {
  Mytolac: { logo: MytolacLogo, width: '8.125rem', height: '3.125rem', averageFee: 20, }
}

const pharmaProgramsData = computed(() => {
  const programs = valueData.value?.pharmaprograms || []
  const mapped = programs.map((program) => ({
    ...program,
    averageFee:
      arrotexBrandConfigs[program.brandName]?.averageFee ??
      (program as any).averageFee ??
      0,
    type: arrotexBrandConfigs[program.brandName] ? 'Arrotex' : 'Other'
  }))

  const arrotex = mapped.filter((p) => p.type === 'Arrotex')
  const others = mapped.filter((p) => p.type === 'Other')

  if (others.length === 0) return arrotex

  // Aggregate scriptsAndClaimsSeries by month
  const allSeries = others.flatMap((p) => p.scriptsAndClaimsSeries)
  const seriesByMonth: Record<string, { scripts: number; claims: number; month: string }> = {}

  allSeries.forEach((item) => {
    if (!seriesByMonth[item.month]) {
      seriesByMonth[item.month] = { scripts: 0, claims: 0, month: item.month }
    }
    seriesByMonth[item.month]!.scripts += item.scripts || 0
    seriesByMonth[item.month]!.claims += item.claims || 0
  })

  const aggregatedSeries = Object.values(seriesByMonth).sort((a, b) =>
    a.month.localeCompare(b.month)
  )

  // Breakdown series for each brand in otherBrandConfigs present in data
  const breakdownSeries = Object.keys(otherBrandConfigs)
    .map((brand) => {
      const program = others.find((p) => p.brandName === brand)
      if (!program) return null
      return {
        brandName: brand,
        logo: otherBrandConfigs[brand]!.logo,
        width: otherBrandConfigs[brand]!.width,
        height: otherBrandConfigs[brand]!.height,
        claimsEarnedValue: program.claimsEarnedValue,
        claimsOpportunityValue: program.claimsOpportunityValue,
        averageFee: otherBrandConfigs[brand]!.averageFee
      }
    })
    .filter(Boolean)

  const aggregatedOther: PharmaProgramsProduct = {
    brandName: 'Other',
    claimsEarnedValue: others.reduce((sum, p) => sum + (p.claimsEarnedValue || 0), 0),
    claimsEarnedValueCurrentQuarter: others.reduce(
      (sum, p) => sum + (p.claimsEarnedValueCurrentQuarter || 0),
      0
    ),
    claimsOpportunityValue: others.reduce((sum, p) => sum + (p.claimsOpportunityValue || 0), 0),
    scripts: others.reduce((sum, p) => sum + (p.scripts || 0), 0),
    claims: others.reduce((sum, p) => sum + (p.claims || 0), 0),
    scriptsVersusPreviousQuarter: others.reduce(
      (sum, p) => sum + (p.scriptsVersusPreviousQuarter || 0),
      0
    ),
    claimsVersusPreviousQuarter: others.reduce(
      (sum, p) => sum + (p.claimsVersusPreviousQuarter || 0),
      0
    ),
    scriptsAndClaimsSeries: aggregatedSeries,
    type: 'Other',
    breakdownSeries
  }

  return [...arrotex, aggregatedOther]
})

const chartMaxHeight = computed(() => {
  const heights: Record<string, number> = {}

  pharmaProgramsData.value.forEach((program) => {
    const maxHeight =
      Math.max(...program.scriptsAndClaimsSeries.map((series) => series.scripts)) * 1.5

    heights[program.brandName] = maxHeight
  })

  return heights
})
</script>

<template>
  <SpinnerOverlay v-if="isValueDataStoreLoading" />
  <ErrorView v-else-if="isError" />
  <ContentView v-else>
    <template #top>
      <!-- <DataRefreshDatesButton /> -->
      <ExternalLinks />
      <AxeLogo width="6.5625rem" height="3.125rem" :fill="themeColors.colorText" />
      <PharmaProgramsLogo width="20.3125rem" height="3.125rem" :fill="themeColors.colorText" />
      <ExportButton />
    </template>
    <template #middle>
      <div class="widgets-container">
        <PharmaProgramsWidget
          v-for="program in pharmaProgramsData"
          :key="program.brandName"
          :isMain="program.brandName === 'Other'"
          :data="program"
          :color="arrotexBrandConfigs[program.brandName]?.color || themeColors.colorText"
          :chartMaxHeight="chartMaxHeight[program.brandName]"
          :glow="arrotexBrandConfigs[program.brandName]?.glow || false"
        >
          <template #logo>
            <template v-if="typeof arrotexBrandConfigs[program.brandName]?.logo === 'string'">
              <span
                class="title"
                :style="{
                  width: arrotexBrandConfigs[program.brandName]?.width,
                  height: arrotexBrandConfigs[program.brandName]?.height
                }"
              >
                {{ arrotexBrandConfigs[program.brandName]?.logo }}
              </span>
            </template>
            <component
              v-else
              :is="arrotexBrandConfigs[program.brandName]?.logo"
              :width="arrotexBrandConfigs[program.brandName]?.width"
              :height="arrotexBrandConfigs[program.brandName]?.height"
              :fill="themeColors.colorText"
              :style="{
                transform: arrotexBrandConfigs[program.brandName]?.transform || 'none',
                margin: '1.5625rem'
              }"
            />
          </template>
        </PharmaProgramsWidget>
      </div>
    </template>
  </ContentView>
</template>

<style scoped>
.widgets-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  width: 100%;
  gap: 2%;
  height: 100%;
}

.title {
  font-size: var(--font-size-h2);
  font-weight: bold;
  color: var(--color-background-bright);
  display: inline-block;
  margin: 1.5625rem;
}
</style>
